"use client";

import axios from "axios";
import { useRef, useState, type ChangeEvent, type FormEvent } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import Notification from "@/ui/components/Notification";
import { Emphasis } from "@/ui/atoms/Emphasis";
import { logEvent } from "@/ui/analytics/google";

const API_URL = process.env.NEXT_PUBLIC_MICROGRAPHER_API_URL;
const API_KEY = process.env.NEXT_PUBLIC_MICROGRAPHER_API_KEY;
const RECAPTCHA_SITE_KEY = process.env.NEXT_PUBLIC_RECAPTCHA_KEY_PUBLIC ?? "";

interface FormData {
	name: string;
	email: string;
	message: string;
}

export default function ContactUs() {
	const [formData, setFormData] = useState<FormData>({
		name: "",
		email: "",
		message: "",
	});

	const [status, setStatus] = useState<string>("");
	const [showNotification, setShowNotification] = useState<boolean>(false);
	const [notificationMessage, setNotificationMessage] = useState<string>("");
	const [notificationType, setNotificationType] = useState<"success" | "error">("success");

	const recaptchaRef = useRef<ReCAPTCHA | null>(null);

	const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		const { name, value } = e.target;
		setFormData({ ...formData, [name]: value });
	};

	const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		if (!recaptchaRef.current) {
			setStatus("reCAPTCHA failed to load. Please try again.");
			return;
		}

		setStatus("Verifying reCAPTCHA...");
		recaptchaRef.current.execute(); // Trigger the invisible reCAPTCHA
	};

	const handleCaptchaResponse = async (captchaToken: string | null) => {
		if (!captchaToken) {
			setStatus("CAPTCHA verification failed. Please try again.");
			return;
		}

		setStatus("Sending...");
		try {
			// Log click event to GA
			logEvent("Click", "Contact message send", "Contact Page");

			const response = await axios.post(
				`${API_URL}/contact`,
				{
					...formData,
					token: captchaToken,
				},
				{
					withCredentials: true,
					headers: {
						"x-api-key": API_KEY,
						"Content-Type": "application/json",
					},
				},
			);

			if (response.status === 200) {
				setStatus("");
				setFormData({
					name: "",
					email: "",
					message: "",
				});
				setNotificationMessage("Message Sent!");
				setNotificationType("success");
				setShowNotification(true);
				setTimeout(() => setShowNotification(false), 5000);
			} else {
				setNotificationMessage("Message failed. Try again.");
				setNotificationType("error");
				setShowNotification(true);
				setTimeout(() => setShowNotification(false), 5000);
			}
		} catch (error) {
			console.error(error);
			setStatus("Failed to send email.");
			setNotificationMessage("Failed to send the message. Please try again.");
			setNotificationType("error");
			setShowNotification(true);
			setTimeout(() => setShowNotification(false), 5000);
		}
	};

	const fieldClass =
		"w-full border-0 border-b border-mg-black/20 bg-transparent px-0 py-2 text-mg-black placeholder:text-ink-faint focus:border-mg-gold-deep focus:ring-0";

	return (
		<section className="mx-auto max-w-gallery px-5 py-section-sm sm:px-8">
			<div className="grid gap-12 lg:grid-cols-[2fr_3fr] lg:gap-16">
				{/* Left: invitation + the B2B/commissions line. */}
				<div>
					<p className="label-caps mb-4 text-ink-muted">Get in touch</p>
					<h1 className="font-serif text-display font-light leading-tight text-mg-black">
						We&apos;d love to <Emphasis mode="italic">hear from you.</Emphasis>
					</h1>
					<p className="mt-6 max-w-prose leading-relaxed text-ink-muted">
						Questions about a piece, an edition, or a commission — write to us and we&apos;ll reply
						personally.
					</p>

					<div className="mt-10 border-t border-mg-black/10 pt-8">
						<p className="label-caps text-ink-faint">
							Trade &amp; Commissions <span className="text-mg-gold-deep">(Coming Soon)</span>
						</p>
						<p className="mt-3 max-w-prose text-sm leading-relaxed text-ink-muted">
							For interior designers, hospitality, and bespoke commissions — including crystallizing a
							compound of your own. Mention &ldquo;Trade&rdquo; in your message and we&apos;ll be in touch as
							the programme opens.
						</p>
					</div>
				</div>

				{/* Right: the form. */}
				<div>
					<form className="space-y-8" onSubmit={handleSubmit}>
						<div>
							<label htmlFor="name" className="label-caps mb-2 block text-ink-muted">
								Name
							</label>
							<input
								id="name"
								name="name"
								type="text"
								required
								value={formData.name}
								onChange={handleChange}
								className={fieldClass}
								placeholder="Your name"
							/>
						</div>
						<div>
							<label htmlFor="email" className="label-caps mb-2 block text-ink-muted">
								Email
							</label>
							<input
								id="email"
								name="email"
								type="email"
								autoComplete="email"
								required
								value={formData.email}
								onChange={handleChange}
								className={fieldClass}
								placeholder="you@example.com"
							/>
						</div>
						<div>
							<label htmlFor="message" className="label-caps mb-2 block text-ink-muted">
								Message
							</label>
							<textarea
								id="message"
								name="message"
								rows={6}
								required
								value={formData.message}
								onChange={handleChange}
								className={fieldClass}
								placeholder="How can we help?"
							/>
						</div>

						<ReCAPTCHA
							ref={recaptchaRef}
							sitekey={RECAPTCHA_SITE_KEY}
							size="invisible"
							onChange={handleCaptchaResponse}
							onErrored={() => setStatus("reCAPTCHA encountered an error. Please try again.")}
							onExpired={() => setStatus("reCAPTCHA expired. Please refresh and try again.")}
						/>

						<button
							type="submit"
							className="label-caps w-full bg-mg-black px-7 py-4 text-bone transition-colors duration-300 ease-gallery hover:bg-mg-gold hover:text-mg-black sm:w-auto sm:px-16"
						>
							Send Message
						</button>
						{status ? <p className="text-sm text-ink-muted">{status}</p> : null}
					</form>
					<Notification message={notificationMessage} show={showNotification} type={notificationType} />
				</div>
			</div>
		</section>
	);
}
